import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageHeader } from '@/components/layout/page-header';

type Role = 'admin' | 'sbu' | 'finance' | 'viewer';

export function AdminUserNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [deptIds, setDeptIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { data: deptsResp } = useQuery({
    queryKey: ['admin-departments'],
    queryFn: () => api.listDepartments(),
  });

  const create = useMutation({
    mutationFn: () => api.createUser({ email, name, role, departmentIds: deptIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('Đã tạo user');
      navigate('/admin/users');
    },
    onError: (err: Error) => {
      const msg = err.message;
      if (/409/.test(msg)) setError('Email đã tồn tại.');
      else if (/400/.test(msg)) setError('Dữ liệu không hợp lệ.');
      else setError(`Lỗi: ${msg}`);
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    create.mutate();
  }

  function toggleDept(id: number) {
    setDeptIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  return (
    <div className="space-y-4">
      <Link
        to="/admin/users"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft size={14} />
        Back to list
      </Link>
      <PageHeader title="Add user" />

      <Card>
        <form onSubmit={onSubmit} className="space-y-3 p-4 text-sm">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@kaopiz.com"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Họ Tên"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="role">Role</Label>
            <Select value={role} onValueChange={(v) => setRole(v as Role)}>
              <SelectTrigger id="role">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="viewer">viewer</SelectItem>
                <SelectItem value="finance">finance</SelectItem>
                <SelectItem value="sbu">sbu</SelectItem>
                <SelectItem value="admin">admin</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <fieldset className="block">
            <legend className="text-sm font-medium text-muted-foreground">Departments</legend>
            <div className="mt-1 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {deptsResp?.rows?.map((d) => (
                <label key={d.id} className="flex items-center gap-2 text-foreground">
                  <input
                    type="checkbox"
                    checked={deptIds.includes(d.id)}
                    onChange={() => toggleDept(d.id)}
                  />
                  <span className="font-mono text-xs text-muted-foreground">{d.code}</span>
                  <span className="truncate">{d.name}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {error && (
            <div className="rounded border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Link to="/admin/users">
              <Button type="button" variant="secondary" size="default">
                Cancel
              </Button>
            </Link>
            <Button type="submit" variant="default" size="default" disabled={create.isPending}>
              {create.isPending ? 'Đang tạo…' : 'Create user'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
